import { UploadSimple } from "@phosphor-icons/react";
import { DOC_TYPES, TEDCApplicant, TQuestionWithAnswer, useEDCFlow } from "@smvtech/x-flux";
import { Tooltip } from "antd";
import toast from "components/common/Toaster";
import { MIXPANEL_EVENTS } from "constants/mixpanelEvents";
import Mixpanel from "utils/mixpanel";
import {
	BookOpen,
	Camera,
	Hotel,
	Plane,
	CreditCard,
	FileText,
	type LucideIcon,
	Baby,
	Mail,
	ScrollText
} from "lucide-react";
import { useEDCFlowContext } from "pages/EDCFlow/context";
import { useState } from "react";
import { validateAllowedFile } from "utils/validateAllowedFile";

const DOCUMENT_ICON_MAP: Partial<Record<DOC_TYPES, LucideIcon>> = {
	[DOC_TYPES.PASSPORT_FRONT]: BookOpen,
	[DOC_TYPES.PASSPORT_BACK]: BookOpen,
	[DOC_TYPES.PHOTO]: Camera,
	[DOC_TYPES.HOTEL_TICKET]: Hotel,
	[DOC_TYPES.ARRIVAL_FLIGHT_TICKET]: Plane,
	[DOC_TYPES.DEPARTURE_FLIGHT_TICKET]: Plane,
	[DOC_TYPES.PAN_CARD]: CreditCard,
	[DOC_TYPES.BANK_STATEMENT]: FileText,
	[DOC_TYPES.BIRTH_CERTIFICATE]: Baby,
	[DOC_TYPES.COVER_LETTER]: Mail,
	[DOC_TYPES.ITR]: ScrollText,
	[DOC_TYPES.BUSINESS_ITR]: ScrollText
};

const getDocumentIcon = (docType: DOC_TYPES): LucideIcon => {
	return DOCUMENT_ICON_MAP[docType] ?? UploadSimple;
};

type DocumentUploadProps = {
	document: TQuestionWithAnswer;
	activeApplicantData: TEDCApplicant;
	onUploadStart: () => void;
	onUploadProgress: (progress: number) => void;
	onUploadComplete: () => void;
	readOnly?: boolean;
};

const DocumentUpload = ({
	document,
	activeApplicantData,
	onUploadStart,
	onUploadProgress,
	onUploadComplete,
	readOnly = false
}: DocumentUploadProps) => {
	const { updateAnswer, order } = useEDCFlow();
	const { setTncAccepted } = useEDCFlowContext();
	const isDocumentLocked = !!order?.qc_terms_and_conditions;
	const [isDragOver, setIsDragOver] = useState(false);

	const handleDragOver = (e: React.DragEvent) => {
		e.preventDefault();
		if (!isDocumentLocked) setIsDragOver(true);
	};

	const handleDragLeave = (e: React.DragEvent) => {
		e.preventDefault();
		if (e.currentTarget.contains(e.relatedTarget as Node)) return;
		setIsDragOver(false);
	};

	const handleDrop = (e: React.DragEvent) => {
		e.preventDefault();
		setIsDragOver(false);
		if (!isDocumentLocked) handleFileUpload(e.dataTransfer.files);
	};

	const handleFileUpload = async (files: FileList | null) => {
		if (!files || files.length === 0) return;

		const file = files[0];

		const fileError = await validateAllowedFile(file);
		if (fileError) {
			toast.error(fileError);
			return;
		}

		const isReupload = !!document.answer_data?.assets_id?.length;
		onUploadStart();

		try {
			await updateAnswer(
				activeApplicantData.application_id,
				document.question.type,
				document.question.question,
				{
					visa_question_id: document.id,
					answer: file.name,
					traveller_id: activeApplicantData.traveller._id,
					file: file
				},
				(event) => {
					onUploadProgress(event.percent || 0);
				}
			);
			Mixpanel.track(MIXPANEL_EVENTS.EDC_FLOW.DOCUMENT_UPLOADED, {
				applicationId: activeApplicantData.application_id,
				documentId: document.id,
				documentType: document.question.doc_type,
				document: document.question.question,
				reupload: isReupload
			});
			onUploadComplete();
		} catch (error) {
			console.error("Error uploading document:", error);
			onUploadComplete();
		} finally {
			setTncAccepted(false);
		}
	};

	const DocIcon = getDocumentIcon(document.question.doc_type as DOC_TYPES);

	if (readOnly) {
		return (
			<div className="flex h-full w-full flex-col items-center justify-center">
				<div className="flex h-12 w-12 items-center justify-center rounded-full bg-indigo-500/20">
					<DocIcon size={20} className="text-indigo-500" strokeWidth={2.5} aria-hidden="true" />
				</div>
			</div>
		);
	}

	return (
		<Tooltip
			styles={{ body: { width: isDocumentLocked ? "200px" : "320px" } }}
			title={isDocumentLocked ? "Order has already been placed." : null}>
			<label
				onDragOver={handleDragOver}
				onDragLeave={handleDragLeave}
				onDrop={handleDrop}
				className={`flex h-full w-full flex-col items-center justify-center gap-4 rounded-md transition-colors ${
					isDragOver ? "bg-indigo-500/10" : "hover:bg-indigo-500/10"
				} ${isDocumentLocked ? "cursor-not-allowed" : "cursor-pointer"}`}>
				<input
					type="file"
					accept="image/jpeg,image/png,application/pdf"
					hidden
					onChange={(e) => handleFileUpload(e.target.files)}
					disabled={isDocumentLocked}
				/>
				<div className="flex h-12 w-12 items-center justify-center rounded-full bg-indigo-500/20">
					<DocIcon size={20} className="text-indigo-500" strokeWidth={2.5} aria-hidden="true" />
				</div>
				<span className="sr-only">Click to upload</span>
			</label>
		</Tooltip>
	);
};

export default DocumentUpload;

